import type { components } from '@tallerya/api-client';
import type { ProblemaApi } from '@tallerya/i18n';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { useTallerDeLaCache } from '../../taller/ProveedorTaller';

// Lo que leen y escriben las pantallas de los gastos, sobre el backend de #142:
// la sección «Gastos de esta orden» de la ficha (#143) y el listado de quien
// lleva la caja (#144).
//
// **El monto no viaja con el gasto**: tiene su endpoint y sus dos permisos,
// `expense.amount.view` y `expense.amount.edit`, con la misma regla que los de la
// orden. Por eso registrar con monto son dos pedidos, y una lista pide los
// montos de sus filas aparte, todos juntos (#144).

export type Gasto = components['schemas']['ExpenseListItem'];
export type FichaGasto = components['schemas']['ExpenseDetail'];
export type MontoGasto = components['schemas']['ExpenseAmount'];
export type ProveedorParaGasto = components['schemas']['SupplierListItem'];
export type EntradaHistorialGasto = components['schemas']['HistoryEntry'];

/// El taller de la pestaña (#205), en toda clave de caché: regla 5 de multitenancy.
function useTaller(): string {
  return useTallerDeLaCache();
}

const claves = {
  todo: (taller: string) => ['gastos', taller] as const,
  deOrden: (taller: string, orden: string) => ['gastos', taller, 'orden', orden] as const,
  ficha: (taller: string, id: string) => ['gastos', taller, 'ficha', id] as const,
  // Bajo `gastos`: cualquier cambio del gasto lo invalida, y la ficha lo ve al momento.
  historial: (taller: string, id: string) => ['gastos', taller, 'historial', id] as const,
  listado: (taller: string, sucursal: string, filtros: FiltrosListadoGastos) =>
    ['gastos', taller, 'listado', sucursal, filtros] as const,
  montos: (taller: string, ids: readonly string[]) => ['gastos', taller, 'montos', ids] as const,
  // Bajo `proveedores`: el alta al vuelo invalida esa rama, y el combo trae al nuevo.
  proveedores: (taller: string) => ['proveedores', taller, 'paraGasto'] as const,
};

/** La versión llega como `number | string` —un `uint` en el contrato—. Se manda como número. */
export const version = (v: number | string) => Number(v);

/** Un monto como llega del contrato —`number | string`, un `decimal`—, o `null` si no hay. */
export const montoDe = (v: number | string | null) => (v === null ? null : Number(v));

const dos = (n: number) => String(n).padStart(2, '0');

/** Hoy en la hora de quien mira, como `2026-10-17`: el día que propone el alta y el pago. */
export function hoy(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
}

/**
 * Un día suelto del contrato —`purchasedOn`, `paidOn`— como fecha local, para
 * formatearlo. **No sirve `new Date('2026-10-17')`**: lo lee como medianoche UTC,
 * y al oeste de Greenwich se mostraría el 16.
 */
export function diaLocal(texto: string): Date {
  const [anio = 0, mes = 1, dia = 1] = texto.split('-').map(Number);
  return new Date(anio, mes - 1, dia);
}

/**
 * Los gastos de una orden, todos: una orden tiene unos pocos, y la sección los
 * muestra juntos. Pide `expense.create`: sin él la API responde 403 y la sección
 * no se dibuja.
 */
export function useGastosDeOrden(orden: string, habilitado: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.deOrden(taller, orden),
    enabled: habilitado,
    queryFn: async () => {
      const todos: Gasto[] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await api.GET('/api/expenses', {
          params: { query: { serviceOrderId: orden, limit: 100, ...(cursor && { cursor }) } },
        });
        if (error || !data) throw new Error('No se pudieron leer los gastos');
        todos.push(...data.items);
        cursor = data.hasMore && data.nextCursor ? data.nextCursor : undefined;
      } while (cursor);
      return todos;
    },
  });
}

async function pedirMonto(id: string): Promise<MontoGasto> {
  const { data, error } = await api.GET('/api/expenses/{id}/amount', { params: { path: { id } } });
  if (error || !data) throw new Error('No se pudo leer el monto del gasto');
  return data;
}

/** Cuántos ids acepta `GET /api/expenses/amounts` de una vez: `PageRequest.LimiteMaximo`. */
const MONTOS_POR_PEDIDO = 100;

/**
 * Los montos de una lista de gastos, en un pedido, por id. **Solo con
 * `expense.amount.view`**: sin él no se pide, y la lista no dibuja montos.
 *
 * Los ids se ordenan para la clave: la misma página no se vuelve a pedir porque
 * llegó en otro orden. Un gasto que no vino —se eliminó entre una lectura y la
 * otra— no está en el mapa, y su fila no dice nada de su monto.
 *
 * **De a 100**, el tope de la API: una página del listado cabe en uno, pero la
 * sección de la orden pide todos sus gastos, y una orden puede pasar de cien.
 */
export function useMontosGastos(ids: readonly string[], habilitado: boolean) {
  const taller = useTaller();
  const ordenados = [...ids].sort();

  return useQuery({
    queryKey: claves.montos(taller, ordenados),
    enabled: habilitado && ordenados.length > 0,
    queryFn: async () => {
      const bloques: string[][] = [];
      for (let i = 0; i < ordenados.length; i += MONTOS_POR_PEDIDO) {
        bloques.push(ordenados.slice(i, i + MONTOS_POR_PEDIDO));
      }
      const respuestas = await Promise.all(
        bloques.map((ids) => api.GET('/api/expenses/amounts', { params: { query: { ids } } })),
      );
      const montos = new Map<string, MontoGasto>();
      for (const { data, error } of respuestas) {
        if (error || !data) throw new Error('No se pudieron leer los montos de los gastos');
        for (const m of data) montos.set(String(m.id), m);
      }
      return montos;
    },
    // Pasar de página no deja la columna en blanco mientras llegan los nuevos.
    placeholderData: keepPreviousData,
  });
}

/** Qué junta el listado (#144): lo que falta pagar, lo que falta completar, o todo. */
export type VistaGastos = 'por-pagar' | 'por-completar' | 'todos';

export interface FiltrosListadoGastos {
  vista: VistaGastos;
  cursor: string | null;
}

/**
 * El listado de quien lleva la caja (#144), de la sucursal del app bar, lo más
 * nuevo primero y por cursor. Lo de una orden y lo del taller van juntos.
 */
export function useListadoGastos(sucursal: string, filtros: FiltrosListadoGastos) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.listado(taller, sucursal, filtros),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/expenses', {
        params: {
          query: {
            locationId: sucursal,
            ...(filtros.vista === 'por-pagar' && { pendingPayment: true }),
            ...(filtros.vista === 'por-completar' && { incomplete: true }),
            ...(filtros.cursor && { cursor: filtros.cursor }),
          },
        },
      });
      if (error || !data) throw new Error('No se pudo leer el listado de gastos');
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

/** La ficha de un gasto, o `null` si no existe en este taller. */
export function useFichaGasto(id: string | null) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.ficha(taller, id ?? ''),
    enabled: id !== null,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/expenses/{id}', {
        params: { path: { id: id ?? '' } },
      });
      if (response.status === 404) return null;
      if (error || !data) throw new Error('No se pudo leer el gasto');
      return data;
    },
  });
}

/**
 * El historial del gasto (#189), lo más nuevo primero. El monto llega con sus
 * cifras solo con `expense.amount.view`: sin él, «Cambió: monto».
 *
 * Solo se pide con el panel abierto: no al abrir la ficha (#193).
 */
export function useHistorialGasto(id: string, abierto: boolean) {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.historial(taller, id),
    enabled: abierto,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/expenses/{id}/history', { params: { path: { id } } });
      if (error || !data) throw new Error('No se pudo leer el historial del gasto');
      return data;
    },
  });
}

/**
 * Los proveedores activos, para el combo del gasto. Todos, como las categorías
 * del catálogo: un taller mediano le compra a decenas, no a miles. Un desactivado
 * no recibe gastos nuevos, así que no se ofrece.
 */
export function useProveedoresParaGasto() {
  const taller = useTaller();

  return useQuery({
    queryKey: claves.proveedores(taller),
    queryFn: async () => {
      const todos: ProveedorParaGasto[] = [];
      let cursor: string | undefined;
      do {
        const { data, error } = await api.GET('/api/suppliers', {
          params: { query: { isActive: true, limit: 100, ...(cursor && { cursor }) } },
        });
        if (error || !data) throw new Error('No se pudieron leer los proveedores');
        todos.push(...data.items);
        cursor = data.hasMore && data.nextCursor ? data.nextCursor : undefined;
      } while (cursor);
      return todos.sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 60_000,
  });
}

/** Lo que se manda al registrar o editar: los opcionales vacíos no viajan. */
export interface EnvioGasto {
  locationId: string;
  serviceOrderId: string | null;
  description: string;
  purchasedOn: string;
  supplierId: string;
  documentNumber: string;
  notes: string;
}

/** El cuerpo del contrato: espacios afuera, y un opcional vacío no se manda. */
function cuerpoDe(v: EnvioGasto) {
  const siHay = <K extends string>(clave: K, valor: string | null) =>
    valor?.trim() ? ({ [clave]: valor.trim() } as Record<K, string>) : {};

  return {
    locationId: v.locationId,
    description: v.description.trim(),
    purchasedOn: v.purchasedOn,
    ...siHay('serviceOrderId', v.serviceOrderId),
    ...siHay('supplierId', v.supplierId),
    ...siHay('documentNumber', v.documentNumber),
    ...siHay('notes', v.notes),
  };
}

/**
 * Las escrituras del gasto. Como las de la orden: devuelven el `ProblemDetails` o
 * nada, llevan su `Idempotency-Key` e invalidan **todo** lo de gastos —el monto
 * y la ficha comparten la versión de la fila, y cualquier cambio la mueve—.
 */
export function useCambiosGasto() {
  const taller = useTaller();
  const cache = useQueryClient();

  async function terminar(error: unknown) {
    await cache.invalidateQueries({ queryKey: claves.todo(taller) });
    return error ? (error as ProblemaApi) : undefined;
  }

  return {
    /** Devuelve el id del gasto nuevo, o por qué no se registró. */
    registrar: async (clave: string, v: EnvioGasto): Promise<{ id?: string; problema?: ProblemaApi }> => {
      const { data, error } = await api.POST('/api/expenses', {
        headers: { 'Idempotency-Key': clave },
        body: cuerpoDe(v),
      });
      if (error || !data) return { problema: error as ProblemaApi };
      await cache.invalidateQueries({ queryKey: claves.todo(taller) });
      return { id: String(data.id) };
    },

    /** El gasto entero como debe quedar: un opcional vacío lo deja vacío. */
    editar: async (clave: string, id: string, versionLeida: number, v: EnvioGasto) => {
      const { error } = await api.PUT('/api/expenses/{id}', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id } },
        body: { version: versionLeida, ...cuerpoDe(v) },
      });
      return terminar(error);
    },

    /** El monto y la versión de la fila, leídos ahora, sin caché: el alta los pide recién registrado. */
    leerMonto: pedirMonto,

    /**
     * Con la versión que se leyó: la del alta recién hecha, o la que trajo la
     * ventana del monto. `null` lo deja sin monto: el campo no viaja.
     */
    cambiarMonto: async (clave: string, id: string, versionLeida: number, monto: number | null) => {
      const { error } = await api.PUT('/api/expenses/{id}/amount', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id } },
        body: { version: versionLeida, ...(monto !== null && { amount: monto }) },
      });
      return terminar(error);
    },

    pagar: async (clave: string, id: string, dia: string) => {
      const { error } = await api.PUT('/api/expenses/{id}/payment', {
        headers: { 'Idempotency-Key': clave },
        params: { path: { id } },
        body: { paidOn: dia },
      });
      return terminar(error);
    },

    // Quitar el pago lleva una clave nueva por toque, como cumplir una línea: no
    // tiene cuerpo que distinga un pedido del anterior. Eliminar no la lleva: el
    // endpoint no la declara, y el doble clic lo frena la ventana que confirma.

    quitarPago: async (id: string) => {
      const { error } = await api.DELETE('/api/expenses/{id}/payment', {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        params: { path: { id } },
      });
      return terminar(error);
    },

    eliminar: async (id: string) => {
      const { error } = await api.DELETE('/api/expenses/{id}', { params: { path: { id } } });
      // Rechazado —otra persona ya lo eliminó, por ejemplo—: todo de nuevo, para que la pantalla se ponga al día.
      if (error) return terminar(error);
      // Todo menos la ficha que se acaba de eliminar y su historial: pedirlos de
      // nuevo daría un 404 justo mientras la ficha abierta sale (#190).
      const eliminadas = [claves.ficha(taller, id), claves.historial(taller, id)];
      await cache.invalidateQueries({
        queryKey: claves.todo(taller),
        predicate: (consulta) =>
          !eliminadas.some((clave) => clave.every((parte, i) => consulta.queryKey[i] === parte)),
      });
      return undefined;
    },
  };
}
